import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

function LogoMark() {
  return (
    <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-[26px] bg-gradient-to-br from-[#ecfaf5] via-[#e2f7f1] to-[#f8efe3] border border-white/80 shadow-[0_18px_40px_rgba(15,23,42,0.08)] overflow-hidden">
      <div className="absolute inset-[8px] rounded-[20px] border border-white/70" />

      <div className="absolute top-2 left-2 w-6 h-6 border-t-[3px] border-l-[3px] border-[#7dc8c0] rounded-tl-[8px]" />
      <div className="absolute bottom-2 left-2 w-7 h-7 border-b-[3px] border-l-[3px] border-[#7dc8c0] rounded-bl-[10px]" />
      <div className="absolute top-2 right-2 w-7 h-7 border-t-[3px] border-r-[3px] border-[#e7c86d] rounded-tr-[10px]" />
      <div className="absolute bottom-2 right-2 w-7 h-7 border-b-[3px] border-r-[3px] border-[#e7c86d] rounded-br-[10px]" />

      <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#7dc8c0] shadow-[0_0_12px_rgba(125,200,192,0.55)]" />
      <div className="absolute right-1.5 top-[28%] w-2 h-2 rounded-full bg-[#e7c86d] shadow-[0_0_12px_rgba(231,200,109,0.55)]" />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#e7c86d] shadow-[0_0_12px_rgba(231,200,109,0.55)]" />

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex items-end leading-none select-none">
          <span
            className="font-display text-[3.9rem] sm:text-[4.4rem] font-semibold tracking-[-0.06em]"
            style={{
              background: "linear-gradient(180deg,#5C7CA3 0%,#83AEC9 55%,#B9D8E9 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            R
          </span>
          <span
            className="font-display text-[3.9rem] sm:text-[4.4rem] font-semibold tracking-[-0.08em]"
            style={{
              background: "linear-gradient(180deg,#F3DA8B 0%,#E7C86D 48%,#C9A44C 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            B
          </span>
        </div>
      </div>

      <div className="absolute inset-x-6 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />
    </div>
  );
}

const features = [
  { icon: "📊", label: "Saldo i zbiórki" },
  { icon: "🧾", label: "Rozliczenia wydatków" },
  { icon: "📅", label: "Planowane płatności" },
  { icon: "🔔", label: "Przypomnienia" },
];

export default function IntroScreen() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(interval);
          return 100;
        }
        return p + 2;
      });
    }, 40);

    const timer = setTimeout(() => {
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 500);
    }, 2600);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [navigate]);

  function skip(e) {
    e.stopPropagation();
    setDone(true);
    setTimeout(() => navigate("/login", { replace: true }), 200);
  }

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-[#f6fbfa]"
        >
          {/* Tło */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(167,243,208,0.28),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(253,224,171,0.24),_transparent_26%),radial-gradient(circle_at_bottom_left,_rgba(186,230,253,0.20),_transparent_30%),linear-gradient(180deg,#f8fcfb_0%,#eef8f6_100%)]" />
            <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
            <div className="absolute top-[8%] right-[8%] h-48 w-48 rounded-full bg-amber-200/30 blur-3xl" />
            <div className="absolute bottom-[6%] right-[4%] h-80 w-80 rounded-full bg-teal-200/25 blur-3xl" />
            <div className="absolute bottom-[20%] left-[6%] h-56 w-56 rounded-full bg-sky-200/20 blur-3xl" />
            {/* Subtle lines */}
            <div className="absolute left-[10%] top-[46%] h-[1px] w-[34%] rounded-full bg-emerald-200/70" />
            <div className="absolute left-[12%] top-[49%] h-[1px] w-[26%] rounded-full bg-sky-200/60" />
            {/* Dots */}
            <div className="absolute left-[8%] top-[44%] h-2 w-2 rounded-full bg-teal-300/70 shadow-[0_0_16px_rgba(45,212,191,0.35)]" />
            <div className="absolute right-[14%] top-[28%] h-3 w-3 rounded-full bg-amber-300/70 shadow-[0_0_18px_rgba(251,191,36,0.30)]" />
          </div>

          {/* Zawartość */}
          <div className="relative flex flex-col items-center gap-8 px-6 text-center max-w-md">

            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.72, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
              className="rounded-[30px] bg-white/70 p-3 shadow-[0_18px_48px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100/80"
            >
              <LogoMark />
            </motion.div>

            {/* Teksty */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.55 }}
              className="space-y-3"
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.20em] text-emerald-700/75">
                Rozliczenia klasy
              </div>

              <h1 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">
                <span
                  style={{
                    background: "linear-gradient(135deg,#10b981 0%,#0d9488 50%,#c9a44c 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Skarbnik
                </span>{" "}
                <span className="text-slate-900">klasowy</span>
              </h1>

              <p className="max-w-sm text-sm leading-6 text-slate-500 md:text-base">
                Przejrzysty panel zbiórek, wydatków i rozliczeń w nowoczesnej formie.
              </p>
            </motion.div>

            {/* Feature pills */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45 }}
              className="flex flex-wrap justify-center gap-2"
            >
              {features.map((f, i) => (
                <motion.span
                  key={f.label}
                  initial={{ opacity: 0, scale: 0.88 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.55 + i * 0.06, duration: 0.35 }}
                  className="flex items-center gap-1.5 rounded-full border border-white/70 bg-white/75 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-[0_4px_12px_rgba(15,23,42,0.05)]"
                >
                  <span>{f.icon}</span>
                  {f.label}
                </motion.span>
              ))}
            </motion.div>

            {/* Progress + Skip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="w-64 space-y-3"
            >
              <div className="h-1.5 overflow-hidden rounded-full bg-white/70 shadow-inner">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-amber-300"
                  style={{ width: `${progress}%` }}
                  transition={{ ease: "linear" }}
                />
              </div>

              <button
                type="button"
                onClick={skip}
                className="text-xs font-medium text-slate-400 transition-colors hover:text-slate-600"
              >
                Pomiń intro
              </button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
